import React, { useState, useEffect } from "react";
import {
  Modal,
  NumberInput,
  InlineNotification,
  Form,
  FormGroup,
  Stack,
  Tooltip,
  Tile,
  Tag,
} from "@carbon/react";
import { Information } from "@carbon/react/icons";
import fuelIcon from "../../assets/icons/fuel.svg";
import "./clientDepositModal.scss";
import { useTranslation } from "react-i18next";
import { queryClient } from "../..";
import { useAddClientDeposit } from "../../hooks/useAddClientDeposit";
import { useAddClientSettings } from "../../hooks/useAddClientSettings";
import { useSetClientBalance } from "../../hooks/useSetClientBalance";
import ReactGA from "react-ga4";

export default function ClientDepositModal({
  open,
  setOpen,
  editingClient,
  setEditingClient,
}) {
  const [deposit, setDeposit] = useState(0);
  const [balance, setBalance] = useState(editingClient?.balance || 0);
  const [cost, setCost] = useState(
    Number((editingClient?.cost * 100).toPrecision(12)) || 0,
  );
  // const [discount, setDiscount] = useState(editingClient?.discount || 0);
  const [isValid, setIsValid] = useState(true);
  const [notification, setNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const depositMutation = useAddClientDeposit();
  const settingsMutation = useAddClientSettings();
  const balanceMutation = useSetClientBalance();

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: "ClientDepositSettlementsModal",
      firebase_screen_class: "ChatRelationsTable",
    });
  }, []);

  const validate = (values) => {
    const { deposit, cost } = values;

    const d = Number(deposit);
    const c = Number(cost);
    // const dis = Number(discount);

    if (isNaN(d) || isNaN(c)) return false;

    if (d < 0) return false;
    if (c < 0) return false;
    // if (dis < 0 || dis > 100) return false;

    return true;
  };

  useEffect(() => {
    setIsValid(
      validate({
        deposit,
        cost,
        // discount,
      }),
    );
  }, [deposit, cost]);

  const handlePreventE = (evt) => {
    if (evt.key === "e") evt.preventDefault();
  };

  const handleDepositSettlement = async () => {
    if (submitting) return;
    setSubmitting(true);

    const isDepositChanged = Number(deposit) > 0;
    const isCostChanged =
      Number(cost) !== Number((editingClient?.cost * 100).toPrecision(12));
    // const isDiscountChanged = Number(discount) !== editingClient?.discount;
    const isBalanceChanged = Number(balance) !== editingClient?.balance;

    if (
      !isDepositChanged &&
      !isCostChanged &&
      // !isDiscountChanged &&
      !isBalanceChanged
    ) {
      handleCloseModal();
      return;
    }

    try {
      if (isDepositChanged) {
        await depositMutation.mutateAsync({
          guid: editingClient.guid,
          amount: Number(deposit),
        });
      }

      if (isCostChanged) {
        await settingsMutation.mutateAsync({
          guid: editingClient.guid,
          cost: Number((Number(cost) / 100).toFixed(5)),
          autoapprove: editingClient.autoapprove,
          // discount: Number(discount),
        });
      }

      if (isBalanceChanged) {
        await balanceMutation.mutateAsync({
          guid: editingClient.guid,
          amount: Number(balance),
        });
      }

      handleCloseModal();

      queryClient.invalidateQueries({
        queryKey: ["getInfo"],
      });
    } catch (e) {
      setSubmitting(false);
      setNotification({
        visibility: true,
        title: "authentication.notification.requestError500.title",
        subtitle: "authentication.notification.requestError500.serverError",
        kind: "error",
      });
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditingClient(null);
    setDeposit(0);
    setCost(0);
    setSubmitting(false);
    // setDiscount(0);
  };

  return (
    <Modal
      open={!!open}
      onRequestClose={handleCloseModal}
      modalHeading={`${t("main.chat.depositModal.heading")}`}
      modalLabel={`${t("main.chat.depositModal.label")}`}
      primaryButtonText={`${t("main.chat.depositModal.submit")}`}
      primaryButtonDisabled={!isValid || submitting}
      secondaryButtonText={`${t("main.chat.addModal.cancel")}`}
      onRequestSubmit={handleDepositSettlement}
      onSecondarySubmit={handleCloseModal}
      closeButtonLabel={`${t("main.modalText.close")}`}
      className="client_settlement"
    >
      <Tile className="client_settlement_content">
        <div className="client_settlement_content_client-info">
          <span>{`${t("main.chat.depositModal.client", { client: editingClient?.client })}`}</span>
          <span>{editingClient?.guid}</span>
        </div>
      </Tile>
      <Stack gap={6} className="client_settlement_params">
        <Form>
          <FormGroup
            // legendText={t("main.chat.depositModal.priceLegend")}
            legendText={""}
            className="priceDiscount_params"
          >
            <NumberInput
              id="cost"
              label={
                <span className="cost_label">
                  {t("main.chat.depositModal.price")}
                  <Tooltip
                    label={t("main.chat.depositModal.priceDesc")}
                    autoAlign
                  >
                    <Information />
                  </Tooltip>
                </span>
              }
              value={cost}
              min={0}
              onChange={(evt, { value }) => setCost(value)}
              onKeyDown={handlePreventE}
              allowEmpty
              hideSteppers
              step={0.001}
              invalid={isNaN(Number(cost)) || Number(cost) < 0}
              invalidText={t("main.chat.addModal.validationText.invalidValue")}
            />
          </FormGroup>
        </Form>
      </Stack>
      {notification.visibility && (
        <InlineNotification
          title={t(notification.title)}
          subtitle={t(notification.subtitle)}
          kind={notification.kind}
          onClose={() =>
            setNotification({
              visibility: false,
              title: "",
              subtitle: "",
              kind: "",
            })
          }
          lowContrast={true}
          aria-label={t("authentication.notification.closeNotification")}
          className="notification"
        />
      )}
    </Modal>
  );
}
